import { Component, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { TaggingService } from '../../services/tagging.service';
import { HttpParams } from '@angular/common/http';
import { DataSharingService } from '../../services/data-sharing.service';
import { publicMethods } from '../../globals';
import { CommunicationService } from '../../services/communication.service';
import { DashboardBulkSelection, selectionCount, selectionIds, selectionParams } from '../../types/dashboard-bulk-selection';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
declare var $: any;

type MatcherContextType = 'selectedOnPage' | 'all';

@Component({
    selector: 'tagging',
    templateUrl: './tagging.component.html',
    styleUrls: ['./tagging.component.css'],
    standalone: false
})
export class TaggingComponent implements OnInit {
  @Input() modalId = 'tagModal';
  private readonly destroyed = new Subject<void>();
  selection: DashboardBulkSelection;
  readonly selectionCount = selectionCount;
  @Input('selection') set dashboardSelection(value: DashboardBulkSelection) {
    this.selection = value;
    if (value) { this.companyIds = selectionIds(value); this.searchParams = selectionParams(value); }
  }
  selectedTagToSave: number | null = null;
  userTags: Array<any> = null;
  newTagName = '';
  companyIds: Array<any> = [];
  teamMembers: Array<any> = [];
  teamMemberIdToCreateTagFor: number = null;
  searchParams: HttpParams = null;
  countOfSuccessfulTags: number;
  countOfSuccessfulTagRemovals: number;
  tagIdToDelete: number;

  isSuperUser: boolean;
  emailOnAccount: string;
  isEditingCollaborativeTags = false;
  matcherContext: { type: MatcherContextType; description?: string } | null =
    null;

  constructor(
    private communicationService: CommunicationService,
    private taggingService: TaggingService,
    private data: DataSharingService,
    private pubMethods: publicMethods,
    private ngZone: NgZone
  ) {}

  isLoading = this.taggingService.isLoading;

  @Input()
  isMatcher = false;

  @Output()
  matcherTag = new EventEmitter<{ tagId: number; context: MatcherContextType }>();

  ngOnInit() {
    this.communicationService.getIsSuperUserMessage().pipe(takeUntil(this.destroyed)).subscribe(isSuperUser => {
      this.isSuperUser = isSuperUser;
    });
    this.communicationService.getEmailMessage().pipe(takeUntil(this.destroyed)).subscribe(accountEmail => {
      this.emailOnAccount = accountEmail;
    });
    this.data.currentSelectedCompanyIds.pipe(takeUntil(this.destroyed)).subscribe(
      companyIds => { if (!this.selection) this.companyIds = companyIds; }
    );
    this.data.currentSelectedSearchParams.pipe(takeUntil(this.destroyed)).subscribe(
      searchParams => { if (!this.selection) this.searchParams = searchParams; }
    );
    this.data.currentSelectedTagIdToDelete.pipe(takeUntil(this.destroyed)).subscribe(
      tagId => (this.tagIdToDelete = tagId)
    );
  }

  ngAfterViewInit(): void {
    const taggingSelf = this;
    $(`#${this.modalId}`).on('show.bs.modal.bulkTagging', () => {
      if (this.userTags == null) this.ngZone.run(() => this._getUserTags());
    });
    $(`#${this.modalId}`).on('hide.bs.modal.bulkTagging', function (e) {
      taggingSelf.clearStatuses();

      taggingSelf.ngZone.run(() => taggingSelf.data.clearTagToDelete());
      taggingSelf.ngZone.run(() => {
        taggingSelf.matcherContext = null;
        taggingSelf.selectedTagToSave = null;
        taggingSelf.newTagName = '';
      });
    });
  }

  clearStatuses(): void {
    this.countOfSuccessfulTagRemovals = null;
    this.countOfSuccessfulTags = null;
  }

  ngOnDestroy(): void {
    this.destroyed.next(); this.destroyed.complete();
    $(`#${this.modalId}`).off('.bulkTagging');
  }

  updateUserTagDetails(data): void {
    this.userTags = data.user_tags;
    this.teamMembers = data.team_members;
    if (data.team_members && data.team_members.length != 0) {
      this.teamMembers
        .filter(usr => usr.isOrganisationAdmin)
        .map(usr => (usr.checkedForViewable = true));
      this.teamMembers
        .filter(usr => usr.isOrganisationAdmin)
        .map(usr => (usr.checkedForEditable = true));
      this.teamMemberIdToCreateTagFor = data.team_members[0].id;
    }
  }

  _getUserTags(): void {
    this.taggingService
      .getUserTags(false)
      .pipe(takeUntil(this.destroyed))
      .subscribe(output => this.updateUserTagDetails(output));
  }

  updateNewTagInList(newTag): void {
    this.userTags.unshift(newTag);
    this.isEditingCollaborativeTags = false;
  }

  updateNewTagInListAndTagCompanies(newTag): void {
    this.updateNewTagInList(newTag);
    this.selectedTagToSave = newTag['id'];
    this.tagCompanies();
  }

  processCreateNewTagResponse(output): void {
    if (output.length == 0) {
      return;
    }
    this.updateNewTagInListAndTagCompanies(output);
  }

  createNewUserTagAndUpdateTags(): void {
    this.taggingService
      .createUserTag(this.newTagName, this.teamMemberIdToCreateTagFor)
      .subscribe(output => this.processCreateNewTagResponse(output));
  }

  createNewOrganisationTagAndUpdateTags(): void {
    this.taggingService
      .createOrganisationTag(
        this.newTagName,
        this.teamMembers
          .filter(user => user.checkedForViewable)
          .map(user => user.userId),
        this.teamMembers
          .filter(user => user.checkedForEditable)
          .map(user => user.userId)
      )
      .subscribe(output => this.updateNewTagInList(output));
  }

  taggedSuccessful(total): void {
    this.countOfSuccessfulTags = total;
    this.countOfSuccessfulTagRemovals = null;

    this.data.companyTagsUpdated.update(flag => (flag = true));
  }

  tagCompanies(): void {
    if (this.selection?.mode === 'none') return;
    if (!this.selectedTagToSave) return;
    if (this.isMatcher && this.matcherContext) {
      if (this.matcherContext) {
        this.matcherTag.emit({
          tagId: this.selectedTagToSave,
          context: this.matcherContext.type,
        });
      }
      $(`#${this.modalId}`).modal('hide');
      return;
    }
    this.taggingService
      .tagOrUntagCompanies(
        this.selectedTagToSave,
        this.companyIds,
        this.searchParams,
        true
      )
      .subscribe(output => this.taggedSuccessful(output.total_added));
  }

  untaggedSuccessful(total): void {
    this.countOfSuccessfulTagRemovals = total;
    this.countOfSuccessfulTags = null;

    this.data.companyTagsUpdated.update(flag => (flag = true));
  }

  untagCompanies(): void {
    if (this.selection?.mode === 'none') return;
    if (!this.selectedTagToSave) return;

    this.taggingService
      .tagOrUntagCompanies(
        this.selectedTagToSave,
        this.companyIds,
        this.searchParams,
        false
      )
      .subscribe(output => this.untaggedSuccessful(output.total));
  }

  tagDeleted(data): void {
    this.pubMethods.showInfoMessage(
      'The tag "' +
        data.name +
        '" was deleted and removed from ' +
        data.count +
        ' organisations.'
    );
    this._getUserTags();
    $(`#${this.modalId}`).modal('hide');
  }

  deleteTag(tagId): void {
    this.taggingService
      .deleteTagById(tagId)
      .subscribe(output => this.tagDeleted(output));
  }

  openMatcherModal(context: { type: MatcherContextType; description?: string }) {
    if (!this.isMatcher) {
      return;
    }
    this.matcherContext = context;
    this.clearStatuses();
    $(`#${this.modalId}`).modal('show');
  }
}
